import { isFarewellTurn } from '@/lib/conversation-model';
import { noyauSitesOfPage } from '@/lib/session-noyau';
import type {
  SessionConversationBubble,
  SessionDetail,
  SessionTurn,
} from '@matter_extractor/shared';

/**
 * What the fil of the conversation is made of once the pages played IN the
 * conversation stand in it (prolongation.md §7, issue #186) - pure, and beside the
 * screen that draws it, the régime of `conversation-model.ts`.
 *
 * ## Where a bubble stands, and why it is not « by the clock » alone
 *
 * The spec says it in one sentence: the pages of the thread are drawn « entre le
 * dernier tour du protocole et l'adieu », and the farewell's own turn is
 * written AFTER the bubbles (§4.4) - the engine holds it back until the last
 * click. So the bubbles are laid after the last turn that is not the goodbye,
 * oldest first (the clock, then the visit), and the goodbye stays last. The rule
 * is positional on purpose: a goodbye whose stamp were a few milliseconds early
 * must not be drawn BEFORE the pages the person went through to reach it.
 *
 * A session whose protocol closed WITHOUT a farewell step simply ends on its
 * bubbles.
 */

/** One entry of the fil: a turn at its place, or a bubble. */
export type FilEntry =
  | { readonly kind: 'turn'; readonly turn: SessionTurn; readonly position: number }
  | { readonly kind: 'bubble'; readonly bubble: SessionConversationBubble };

/** The bubbles of a detail, oldest first; a detail written before the issue has none. */
export function bubblesOf(detail: SessionDetail): readonly SessionConversationBubble[] {
  return [...(detail.conversationBubbles ?? [])].sort(
    (one, other) =>
      Date.parse(one.createdAt) - Date.parse(other.createdAt) || one.visitIndex - other.visitIndex,
  );
}

/** The turns and the bubbles of a session, in the order the fil draws them. */
export function conversationFil(detail: SessionDetail): FilEntry[] {
  const turns = detail.turns.map((turn, position): FilEntry => ({ kind: 'turn', turn, position }));
  const bubbles = bubblesOf(detail).map((bubble): FilEntry => ({ kind: 'bubble', bubble }));
  if (bubbles.length === 0) return turns;

  const last = detail.turns[detail.turns.length - 1];
  const goodbyeLast = last !== undefined && isFarewellTurn(detail.cascade.protocol, last);
  const split = goodbyeLast ? turns.length - 1 : turns.length;
  return [...turns.slice(0, split), ...bubbles, ...turns.slice(split)];
}

/** The 1-based rank of the page in the walk - what Parcours prints as « Page 7 ». */
export function bubblePageRank(bubble: SessionConversationBubble): number {
  return bubble.visitIndex + 1;
}

/** The anchor a link from the parcours lands on: `#bulle-<visite>`. */
export function bubbleAnchor(visitIndex: number): string {
  return `bulle-${visitIndex}`;
}

/** The visit a `#bulle-<visite>` address names, or null when it names none. */
export function bubbleVisitOfHash(hash: string): number | null {
  const found = /^#?bulle-(\d+)$/.exec(hash);
  if (found === null) return null;
  const visit = Number(found[1]);
  return Number.isSafeInteger(visit) ? visit : null;
}

/**
 * Whether the call failed and the page played WITHOUT a bubble (§4.6): the row
 * is written all the same - the failed call is read here - with an empty message.
 */
export function isBubbleFailed(bubble: SessionConversationBubble): boolean {
  return bubble.message.trim() === '';
}

/**
 * What the page showed under the bubble, in a small line - or null when the
 * frozen content the detail carries says nothing.
 *
 * Derived from what the detail knows of the FROZEN page and nothing else: the
 * Noyau blocks the reading places on it (a choice shows two cards, a portrait
 * shows the portrait), and the checkboxes, selections and notes the inventory
 * declares there. Display conditions are the reader's: the line says what the
 * page CAN show, which is why it stays in small print. A click on a card with no
 * choice read still tells that cards were there.
 */
export function bubbleShownLine(
  detail: SessionDetail,
  bubble: SessionConversationBubble,
): string | null {
  const parts: string[] = [];
  const sites = noyauSitesOfPage(detail, bubble.pageId);
  for (const site of sites) {
    if (site.mode === 'portrait') parts.push('Portrait montré');
    else {
      const which = site.mode === 'first-choice' ? 'premier choix' : 'second choix';
      parts.push(`Deux cartes montrées · ${which}`);
    }
  }
  if (bubble.click?.kind === 'card' && !sites.some((site) => site.mode !== 'portrait')) {
    parts.push('Cartes montrées');
  }

  const declared = detail.variables.filter((entry) => entry.page.pageId === bubble.pageId);
  const count = (kind: 'checkbox' | 'selection' | 'note'): number =>
    declared.filter((entry) => entry.kind === kind).length;
  const checkboxes = count('checkbox');
  if (checkboxes > 0) {
    parts.push(checkboxes > 1 ? 'Cases à cocher montrées' : 'Case à cocher montrée');
  }
  const selections = count('selection');
  if (selections > 0) parts.push(selections > 1 ? 'Sélections montrées' : 'Sélection montrée');
  const notes = count('note');
  if (notes > 0) parts.push(notes > 1 ? 'Notes montrées' : 'Note montrée');

  return parts.length === 0 ? null : parts.join(' · ');
}
